import { dayjs } from "@herowcode/utils/date"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronLeft, ChevronRight, Plus, Settings } from "lucide-react"
import { Suspense, useEffect, useState } from "react"

import {
  PendingTransactionsAlert,
  TransactionForm,
  TransactionList,
  TransactionsSummary,
  TransferForm,
} from "@/components/finance"
import { TransactionsPageSkeleton } from "@/components/skeletons/transactions-skeleton"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useOptimizedRefetch } from "@/hooks/use-optimized-refetch"
import {
  categoriesQueryOptions,
  getAccountsSummary,
  listAccounts,
  listTransactions,
} from "@/http/finance"
import { useOrganization } from "@/store/organization"

type TRansactionsSearch = {
  month?: number
  year?: number
  action?: "create" | "transfer"
}

const accountsQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["finance-accounts", orgSlug],
    queryFn: async () => {
      const response = await listAccounts({
        organizationSlug: orgSlug,
      })
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

const transactionsQueryOptions = (
  orgSlug: string,
  month: number,
  year: number,
) =>
  queryOptions({
    queryKey: ["transactions", orgSlug, month, year],
    queryFn: async () => {
      const response = await listTransactions(orgSlug, month, year)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

const accountsSummaryQueryOptions = (
  orgSlug: string,
  month: number,
  year: number,
) =>
  queryOptions({
    queryKey: ["accounts-summary", orgSlug, month, year],
    queryFn: async () => {
      const response = await getAccountsSummary(orgSlug, month, year)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

export const Route = createFileRoute("/_authenticated/$orgSlug/finances/")({
  validateSearch: (search: Record<string, unknown>): TRansactionsSearch => {
    const now = new Date()
    return {
      month: (search.month as number) || now.getMonth() + 1,
      year: (search.year as number) || now.getFullYear(),
      action: search.action as "create" | "transfer" | undefined,
    }
  },
  loaderDeps: ({ search: { month, year } }) => ({ month, year }),
  loader: async ({ context, params, deps: { month, year } }) => {
    const currentMonth = month || new Date().getMonth() + 1
    const currentYear = year || new Date().getFullYear()

    await Promise.all([
      context.queryClient.ensureQueryData(accountsQueryOptions(params.orgSlug)),
      context.queryClient.ensureQueryData(
        transactionsQueryOptions(params.orgSlug, currentMonth, currentYear),
      ),
      context.queryClient.ensureQueryData(
        accountsSummaryQueryOptions(params.orgSlug, currentMonth, currentYear),
      ),
    ])

    return {
      title: "Transações",
      path: `/${params.orgSlug}/finances`,
    }
  },
  component: TransactionsPage,
  pendingComponent: TransactionsPageSkeleton,
})

function TransactionsPage() {
  const { currentOrg } = useOrganization()
  const navigate = Route.useNavigate()
  const path = Route.useLoaderData()
  const { breadcrumbs } = Route.useRouteContext()
  const search = Route.useSearch()
  const { orgSlug } = Route.useParams()
  const optimizedRefetch = useOptimizedRefetch()

  const [transactionFormOpen, setTransactionFormOpen] = useState(
    search.action === "create",
  )
  const [transferFormOpen, setTransferFormOpen] = useState(
    search.action === "transfer",
  )

  const currentMonth = search.month || new Date().getMonth() + 1
  const currentYear = search.year || new Date().getFullYear()

  useEffect(() => {
    breadcrumbs.setCurrentPath(path)
  }, [breadcrumbs, path])

  // Fetch accounts for forms
  const { data: accountsData } = useSuspenseQuery(accountsQueryOptions(orgSlug))

  // Fetch categories for filters
  const { data: categoriesData } = useSuspenseQuery(
    categoriesQueryOptions(orgSlug),
  )

  // Fetch transactions
  const {
    data: transactionsData,
    isLoading,
    refetch,
  } = useSuspenseQuery(
    transactionsQueryOptions(orgSlug, currentMonth, currentYear),
  )

  // Fetch accounts summary
  const { data: summaryData, isLoading: summaryLoading } = useSuspenseQuery(
    accountsSummaryQueryOptions(orgSlug, currentMonth, currentYear),
  )

  const handleFormClose = () => {
    setTransactionFormOpen(false)
    setTransferFormOpen(false)
    navigate({ search: true })
  }

  const handleSuccess = (options?: { close?: boolean }) => {
    refetch()
    // Invalidate pending transactions cache to update the badge
    optimizedRefetch(
      [
        "pending-transactions",
        "accounts-summary",
        "recent-transactions",
        "dashboard",
      ],
      currentOrg?.slug,
    )
    if (options?.close !== false) {
      handleFormClose()
    }
  }

  const handlePreviousMonth = () => {
    const date = dayjs()
      .year(currentYear)
      .month(currentMonth - 1)
    const previousMonth = date.subtract(1, "month")
    navigate({
      search: (prev) => ({
        ...prev,
        month: previousMonth.month() + 1,
        year: previousMonth.year(),
      }),
    })
  }

  const handleNextMonth = () => {
    const date = dayjs()
      .year(currentYear)
      .month(currentMonth - 1)
    const nextMonth = date.add(1, "month")
    navigate({
      search: (prev) => ({
        ...prev,
        month: nextMonth.month() + 1,
        year: nextMonth.year(),
      }),
    })
  }

  const handleCurrentMonth = () => {
    const now = dayjs()
    navigate({
      search: (prev) => ({
        ...prev,
        month: now.month() + 1,
        year: now.year(),
      }),
    })
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">Transações</h1>
            <p className="text-muted-foreground">
              Gerencie suas transações financeiras
            </p>
          </div>

          <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
            <Link
              to="/$orgSlug/settings/finance"
              params={{ orgSlug: currentOrg?.slug || "" }}
            >
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Configuração</span>
              </Button>
            </Link>

            <Button variant="outline" size="sm" onClick={() => setTransferFormOpen(true)}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Transferência</span>
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setTransactionFormOpen(true)
              }}
            >
              <Plus className="h-4 w-4" />
              <span className="ml-2">Nova Transação</span>
            </Button>
          </div>
        </div>

        <Suspense fallback={<Skeleton className="h-16 w-full rounded-lg" />}>
          <PendingTransactionsAlert
            organizationSlug={currentOrg?.slug || ""}
          />
        </Suspense>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button onClick={handlePreviousMonth} className="px-2">
            <ChevronLeft />
          </Button>
          <Button
            disabled={
              currentMonth - 1 === dayjs().month() &&
              currentYear === dayjs().year()
            }
            onClick={handleCurrentMonth}
            className="sm:min-w-48 uppercase"
          >
            {new Date(currentYear, currentMonth - 1).toLocaleDateString(
              "pt-BR",
              {
                month: "long",
                year: "numeric",
              },
            )}
          </Button>
          <Button onClick={handleNextMonth} className="px-2">
            <ChevronRight />
          </Button>
        </div>

        <TransactionsSummary
          summary={summaryData?.summary || []}
          loading={summaryLoading}
        />

        <TransactionList
          transactions={transactionsData?.transactions || []}
          accounts={accountsData || []}
          categories={categoriesData || []}
          organizationSlug={currentOrg?.slug || ""}
          loading={isLoading}
          onRefresh={() => {
            refetch()

            optimizedRefetch(
              [
                "accounts-summary",
                "recent-transactions",
                "pending-transactions",
                "dashboard",
              ],
              currentOrg?.slug,
            )
          }}
        />
      </div>

      <TransactionForm
        organizationSlug={currentOrg?.slug || ""}
        open={transactionFormOpen}
        onOpenChange={setTransactionFormOpen}
        onSuccess={handleSuccess}
      />

      <TransferForm
        organizationSlug={currentOrg?.slug || ""}
        accounts={accountsData || []}
        open={transferFormOpen}
        onOpenChange={setTransferFormOpen}
        onSuccess={handleSuccess}
      />
    </>
  )
}
